import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container, Typography, Button, Box, Grid, Chip,
  Paper, Stack, Alert, CircularProgress, Tabs, Tab,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, TextField, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import { Avatar, AvatarGroup } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ActivityCard from '../components/ActivityCard';
import MapView from '../components/MapView';
import { getTrip, deleteTrip, exportTrip, voteActivity, getTripMembers } from '../services/api';
import { useAuth } from '../App';
import { getTripStatus } from './Home';

const CATEGORY_FILTERS = ['All', 'Restaurant', 'Hotel', 'Attraction', 'Activity', 'Other'];

export default function TripDetail() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [activitySearch, setActivitySearch] = useState('');
  const [members, setMembers] = useState([]);

  const loadTrip = useCallback(async () => {
    try {
      const { data } = await getTrip(tripId);
      setTrip(data);
      getTripMembers(tripId).then(({ data: m }) => setMembers(m)).catch(() => {});
    } catch {
      setError('Failed to load trip.');
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { loadTrip(); }, [loadTrip]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const formatDateShort = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  const handleCopyCode = () => {
    navigator.clipboard.writeText(trip.shareCode);
    setSnackbar('Code copied!');
  };

  const handleCopyLink = () => {
    const joinUrl = `https://www.checkthislist.com/join?code=${trip.shareCode}`;
    navigator.clipboard.writeText(joinUrl);
    setSnackbar('Link copied!');
  };

  const handleExport = async () => {
    try {
      const { data } = await exportTrip(tripId);
      const activities = data.activities || trip.activities || [];
      const tripData = { ...trip, ...data, activities };

      const sorted = [...activities].sort((a, b) =>
        (a.date || '').localeCompare(b.date || '')
      );

      const grouped = {};
      sorted.forEach((a) => {
        const key = a.date || 'Unscheduled';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(a);
      });

      const dayHtml = Object.entries(grouped).map(([date, acts]) => {
        const dateLabel = date === 'Unscheduled' ? 'Unscheduled' :
          new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        const actHtml = acts.map((a) => `
          <div style="margin-bottom:16px;padding:12px;border:1px solid #ddd;border-radius:8px;">
            <h3 style="margin:0 0 4px;">${a.name || ''}</h3>
            ${a.category ? `<span style="display:inline-block;background:#e3f2fd;padding:2px 8px;border-radius:4px;font-size:13px;margin-bottom:8px;">${a.category}</span>` : ''}
            ${a.location ? `<p style="margin:4px 0;color:#555;">Location: ${a.location}</p>` : ''}
            ${a.notes ? `<p style="margin:4px 0;color:#555;">Notes: ${a.notes}</p>` : ''}
            ${a.averageRating != null ? `<p style="margin:4px 0;color:#ff6b35;font-weight:bold;">Rating: ${Number(a.averageRating).toFixed(1)} / 5</p>` : ''}
          </div>
        `).join('');
        return `<div style="margin-bottom:24px;"><h2 style="border-bottom:2px solid #1565c0;padding-bottom:4px;">${dateLabel}</h2>${actHtml}</div>`;
      }).join('');

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${tripData.name} - Itinerary</title>
        <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:800px;margin:0 auto;padding:32px;color:#222;}
        @media print{body{padding:16px;}}</style></head><body>
        <h1 style="color:#1565c0;">${tripData.name}</h1>
        ${tripData.startDate ? `<p style="color:#666;">${formatDate(tripData.startDate)}${tripData.endDate ? ` &ndash; ${formatDate(tripData.endDate)}` : ''}</p>` : ''}
        ${tripData.description ? `<p>${tripData.description}</p>` : ''}
        <hr style="border:none;border-top:1px solid #ccc;margin:24px 0;">
        ${dayHtml || '<p>No activities yet.</p>'}
        <p style="margin-top:32px;font-size:12px;color:#999;text-align:center;">Generated by TripVibe</p>
        </body></html>`;

      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
      }
    } catch {
      setError('Failed to export trip.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTrip(tripId);
      navigate('/');
    } catch {
      setError('Failed to delete trip.');
    }
  };

  const handleVote = async (activityId) => {
    try {
      await voteActivity(tripId, activityId);
      await loadTrip();
    } catch {
      // silently ignore vote errors
    }
  };

  const filteredActivities = useMemo(() => {
    let list = trip?.activities || [];
    if (categoryFilter !== 'All') {
      list = list.filter((a) => a.category === categoryFilter.toLowerCase());
    }
    if (activitySearch.trim()) {
      const q = activitySearch.toLowerCase();
      list = list.filter((a) => a.name?.toLowerCase().includes(q));
    }
    return list;
  }, [trip, categoryFilter, activitySearch]);

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;
  if (error) return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!trip) return null;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>Back</Button>
        <Box display="flex" gap={1}>
          <Tooltip title="Share trip">
            <IconButton color="primary" onClick={() => setShareOpen(true)}><ShareIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Download itinerary">
            <IconButton color="primary" onClick={handleExport}><DownloadIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Delete trip">
            <IconButton color="error" onClick={() => setDeleteOpen(true)}><DeleteIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
          <Typography variant="h4" fontWeight="bold">{trip.name}</Typography>
          {(() => {
            const status = getTripStatus(trip.startDate, trip.endDate);
            return status ? <Chip label={status.label} color={status.color} size="small" /> : null;
          })()}
        </Box>
        {trip.description && <Typography color="text.secondary" gutterBottom>{trip.description}</Typography>}
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {trip.startDate && (() => {
            const startYear = new Date(trip.startDate).getFullYear();
            const endYear = trip.endDate ? new Date(trip.endDate).getFullYear() : startYear;
            let label;
            if (trip.endDate) {
              label = startYear === endYear
                ? `${formatDateShort(trip.startDate)} - ${formatDateShort(trip.endDate)}, ${endYear}`
                : `${formatDate(trip.startDate)} - ${formatDate(trip.endDate)}`;
            } else {
              label = formatDate(trip.startDate);
            }
            return <Chip label={label} variant="outlined" size="small" />;
          })()}
          <Chip label={`${trip.activities?.length || 0} activities`} size="small" />
        </Stack>
        {members.length > 0 && (
          <Box display="flex" alignItems="center" gap={1.5} mt={2}>
            <GroupIcon fontSize="small" color="action" />
            <AvatarGroup max={8} sx={{ '& .MuiAvatar-root': { width: 30, height: 30, fontSize: 13 } }}>
              {members.map((m) => (
                <Tooltip key={m.userId} title={m.displayName}>
                  <Avatar
                    src={m.profilePhotoUrl || undefined}
                    sx={{ bgcolor: m.avatarColor || 'grey.500' }}
                  >
                    {m.displayName?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?'}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
            <Typography variant="body2" color="text.secondary">
              {members.map(m => m.displayName).join(', ')}
            </Typography>
          </Box>
        )}
      </Paper>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Activities" />
          <Tab label="Map" />
        </Tabs>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/trips/${tripId}/activities/new`)}>
          Add Activity
        </Button>
      </Box>

      {tab === 0 && (
        <>
          {trip.activities?.length > 0 && (
            <Box mb={2}>
              <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
                {CATEGORY_FILTERS.map((cat) => (
                  <Chip
                    key={cat}
                    label={cat}
                    variant={categoryFilter === cat ? 'filled' : 'outlined'}
                    color={categoryFilter === cat ? 'primary' : 'default'}
                    onClick={() => setCategoryFilter(cat)}
                    size="small"
                  />
                ))}
              </Stack>
              <TextField
                fullWidth
                size="small"
                placeholder="Search activities by name..."
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
          )}

          {filteredActivities.length > 0 ? (
            <Grid container spacing={2}>
              {filteredActivities.map((activity) => (
                <Grid item xs={12} sm={6} md={4} key={activity.activityId}>
                  <ActivityCard
                    activity={activity}
                    onClick={() => navigate(`/trips/${tripId}/activities/${activity.activityId}`)}
                    onVote={() => handleVote(activity.activityId)}
                    currentUserId={user?.userId}
                  />
                </Grid>
              ))}
            </Grid>
          ) : trip.activities?.length > 0 ? (
            <Box textAlign="center" mt={4}>
              <Typography color="text.secondary">No activities match your filters.</Typography>
            </Box>
          ) : (
            <Box textAlign="center" mt={6}>
              <Typography color="text.secondary" gutterBottom>No activities yet</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/trips/${tripId}/activities/new`)}>
                Add First Activity
              </Button>
            </Box>
          )}
        </>
      )}

      {tab === 1 && (
        <MapView
          activities={trip.activities || []}
          onMarkerClick={(a) => navigate(`/trips/${tripId}/activities/${a.activityId}`)}
          height={500}
        />
      )}

      {/* Share Dialog */}
      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Share Trip</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Share this code with family members to let them join:</Typography>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1} mt={2} mb={2}>
            <Typography
              variant="h4"
              fontWeight="bold"
              letterSpacing={6}
              sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 3, py: 1, borderRadius: 2 }}
            >
              {trip.shareCode}
            </Typography>
            <Tooltip title="Copy code">
              <IconButton onClick={handleCopyCode}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<LinkIcon />}
            onClick={handleCopyLink}
          >
            Copy Join Link
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2000}
        onClose={() => setSnackbar('')}
        message={snackbar}
      />

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Trip?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete "{trip.name}" and all its activities. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
